import { usersApiClient } from './api';

export interface DailyReward {
  day: number;
  reward: string;
  icon: string;
  claimed: boolean;
  points: number;
}

export interface DailyRewardStatus {
  canClaim: boolean;
  consecutiveDays: number;
  dailyRewards: DailyReward[];
  currentDayReward: number;
}

export interface Challenge {
  name: string;
  description: string;
  target: number;
  progress: number;
  completed: boolean;
  reward: number;
  icon: string;
}

export interface DailyRewardClaimResponse {
  rewardPoints: number;
  consecutiveDays: number;
  totalPoints: number;
  level: number;
  nextRewardPoints: number;
}

// iOS App compatible user profile interface
export interface UserProfile {
  auth0Id: string;
  email: string;
  name: string;
  picture?: string;
  displayName?: string;
  totalPoints: number;
  gamesPlayed: number;
  correctAnswers: number;
  totalAnswers: number;
  bestStreak: number;
  currentLevel?: number;
  currentRank?: string;
  currentTier?: string;
}

// iOS App compatible leaderboard entry
export interface LeaderboardEntry {
  _id: string;
  auth0Id: string;
  name: string;
  picture?: string;
  displayName?: string;
  totalPoints: number;
  gamesPlayed: number;
  bestStreak: number;
  currentLevel?: number;
  currentRank?: string;
  currentTier?: string;
}

const userService = {
  // EC2 API - Get user profile (iOS app compatible)
  getUserProfile: async (auth0Id: string): Promise<UserProfile> => {
    try {
      // URL encode the Auth0 ID to handle special characters like '|'
      const encodedAuth0Id = encodeURIComponent(auth0Id);
      const response = await usersApiClient.get(`/users/${encodedAuth0Id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get user profile:', error);
      throw error;
    }
  },

  // EC2 API - Create or update user (iOS app compatible)
  createOrUpdateUser: async (userData: Partial<UserProfile>): Promise<UserProfile> => {
    try {
      const response = await usersApiClient.post('/users', userData);
      return response.data;
    } catch (error) {
      console.error('Failed to create/update user:', error);
      throw error;
    }
  },

  // EC2 API - Get leaderboard (iOS app compatible)
  getLeaderboard: async (timeframe: string = 'allTime', limit: number = 50): Promise<LeaderboardEntry[]> => {
    try {
      const response = await usersApiClient.get(`/leaderboard?timeframe=${timeframe}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get leaderboard:', error);
      throw error;
    }
  },

  // EC2 API - Submit game results (legacy helper; uses Authorization header, no query params)
  submitGameResults: async (gameResult: any): Promise<any> => {
    try {
      const response = await usersApiClient.post('/users/game-results', gameResult);
      return response.data;
    } catch (error) {
      console.error('Failed to submit game results:', error);
      throw error;
    }
  },

  // Legacy methods - keeping for backward compatibility
  getDailyRewardStatus: async (): Promise<DailyRewardStatus> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      const response = await usersApiClient.get('/users/daily-reward/status');
      return response.data.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        console.log('User not authenticated for daily rewards');
        throw new Error('Authentication required');
      }
      console.error('Failed to get daily reward status:', error);
      throw error;
    }
  },

  claimDailyReward: async (): Promise<DailyRewardClaimResponse> => {
    try {
      const response = await usersApiClient.post('/users/daily-reward/claim');
      return response.data.data;
    } catch (error) {
      console.error('Failed to claim daily reward:', error);
      throw error;
    }
  },

  getDailyChallenges: async (): Promise<Challenge[]> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      const response = await usersApiClient.get('/users/challenges/daily');
      return response.data.data.challenges;
    } catch (error) {
      console.error('Failed to get daily challenges:', error);
      throw error;
    }
  },
};

export default userService;